/* Trappbutiken – header (Wubs): megameny, mobil drawer, prediktiv sök */

const wubsMoney = new Intl.NumberFormat('sv-SE', {
  style: 'currency',
  currency: 'SEK',
  maximumFractionDigits: 0,
});

const wubsEscape = (str) =>
  String(str == null ? '' : str).replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]
  );

/* ------------------------------------------------------------------ *
 * Prediktiv sök
 *
 * Två saker Shopifys standard-sök gör dåligt, som vi rättar här:
 *
 * 1. ARTIKLAR UTELÄMNAS om man inte begär resources[type] explicit – och det är
 *    just guiderna kunden söker efter ("montera ledstång").
 *
 * 2. ALLA ORD MÅSTE MATCHA. Ett enda okänt ord nollar hela frågan:
 *      "trappräcke"        → 4 träffar
 *      "trappräcke regler" → 0 träffar   (fast guiden finns!)
 *      "vitpigmenterad ek" → 0 träffar
 *    Scopet §9 kräver uttryckligen att båda ska fungera. Vi löser det med
 *    query-relaxering: ger hela frasen noll så söker vi orden var för sig och
 *    rankar efter hur många ord som träffade.
 * ------------------------------------------------------------------ */
class WubsSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('.wubs-header__search-input');
    this.panel = this.querySelector('.wubs-header__search-panel');
    this.clearBtn = this.querySelector('.wubs-header__search-clear');
    // Startläget ligger i en <template> – dess innehåll är inert och nås via .content
    this.startTpl = this.querySelector('[data-wubs-search-start-src]');
    this.limit = parseInt(this.dataset.limit, 10) || 4;
    this.controller = null;
    this.timer = null;
    this.cache = new Map();

    if (!this.input || !this.panel) return;

    this.input.addEventListener('input', this.onInput.bind(this));
    this.input.addEventListener('focus', () => {
      const term = this.input.value.trim();
      if (term.length >= 2) this.open();
      else this.showStart();
    });
    this.input.addEventListener('keydown', this.onKeydown.bind(this));
    this.clearBtn?.addEventListener('click', () => {
      this.reset();
      this.input.focus();
      this.showStart();
    });

    // Klick på ett populärt sökförslag fyller fältet
    this.addEventListener('click', (e) => {
      const chip = e.target.closest('[data-wubs-search-term]');
      if (!chip) return;
      e.preventDefault();
      this.input.value = chip.dataset.wubsSearchTerm;
      this.input.focus();
      this.onInput();
    });

    document.addEventListener('click', (e) => {
      if (this.contains(e.target)) return;
      // Sökknappen ligger utanför <wubs-search>. Utan det här undantaget bubblar
      // öppningsklicket hit och stänger panelen i samma veva som den öppnas.
      if (e.target.closest?.('[data-wubs-search-toggle]')) return;
      this.close();
    });
  }

  /* --- Startläge innan man skrivit något --- */
  showStart() {
    if (!this.startTpl) return this.close();
    const frag = this.startTpl.content.cloneNode(true);
    // Inget att visa (varken förslag eller genvägar konfigurerade) → visa inte en tom ruta
    if (!frag.querySelector('.wubs-search__group')) return this.close();
    this.panel.innerHTML = '';
    this.panel.appendChild(frag);
    this.open();
  }

  onInput() {
    const term = this.input.value.trim();
    this.clearBtn.hidden = term.length === 0;
    clearTimeout(this.timer);
    if (term.length < 2) {
      this.showStart();
      return;
    }
    this.timer = setTimeout(() => this.search(term), 220);
  }

  onKeydown(e) {
    if (e.key === 'Escape') {
      this.reset();
      this.input.blur();
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Enter'].includes(e.key)) return;
    const items = Array.from(this.panel.querySelectorAll('.wubs-search__item'));
    if (!items.length || this.panel.hidden) return;

    const current = items.findIndex((li) => li.getAttribute('aria-selected') === 'true');
    if (e.key === 'Enter') {
      if (current > -1) {
        e.preventDefault();
        items[current].querySelector('a')?.click();
      }
      return;
    }
    e.preventDefault();
    const next =
      e.key === 'ArrowDown' ? (current + 1) % items.length : (current - 1 + items.length) % items.length;
    items.forEach((li, i) => li.setAttribute('aria-selected', String(i === next)));
    items[next].scrollIntoView({ block: 'nearest' });
  }

  /** Ett anrop mot Shopifys suggest-API med alla resurstyper vi bryr oss om */
  async fetchSuggest(term, signal) {
    const params = new URLSearchParams({ q: term });
    params.append('resources[type]', 'product,collection,page,article,query');
    // Extra produkter så konfiguratorerna kan brytas ut i egen grupp utan att äta platserna
    params.append('resources[limit]', String(this.limit + 4));
    params.append('resources[options][unavailable_products]', 'last');
    const res = await fetch(`${window.Shopify?.routes?.root || '/'}search/suggest.json?${params}`, { signal });
    if (!res.ok) throw new Error(String(res.status));
    const data = await res.json();
    return data?.resources?.results || {};
  }

  static count(r) {
    return ['products', 'collections', 'pages', 'articles', 'queries'].reduce(
      (n, k) => n + (r[k]?.length || 0),
      0
    );
  }

  /** Slår ihop flera resultatset och rankar efter hur många ord som gav träff */
  static merge(sets) {
    const out = {};
    for (const key of ['products', 'collections', 'pages', 'articles', 'queries']) {
      const score = new Map();
      for (const set of sets) {
        for (const item of set[key] || []) {
          const id = item.url || item.title || item.text;
          if (!id) continue;
          const prev = score.get(id);
          if (prev) prev.hits += 1;
          else score.set(id, { item, hits: 1 });
        }
      }
      out[key] = [...score.values()].sort((a, b) => b.hits - a.hits).map((e) => e.item);
    }
    return out;
  }

  async search(term) {
    if (this.cache.has(term)) {
      const c = this.cache.get(term);
      this.render(c.results, term, c.relaxedTo);
      return;
    }
    this.controller?.abort();
    this.controller = new AbortController();
    const signal = this.controller.signal;

    try {
      let results = await this.fetchSuggest(term, signal);
      let relaxedTo = null;

      // Query-relaxering: hela frasen gav noll → sök orden var för sig
      const words = term.split(/\s+/).filter((w) => w.length >= 2);
      if (WubsSearch.count(results) === 0 && words.length > 1) {
        const sets = await Promise.all(
          words.map((w) => this.fetchSuggest(w, signal).catch(() => ({})))
        );
        const hitWords = words.filter((_, i) => WubsSearch.count(sets[i]) > 0);
        if (hitWords.length) {
          results = WubsSearch.merge(sets);
          relaxedTo = hitWords.join(' ');
        }
      }

      this.cache.set(term, { results, relaxedTo });
      this.render(results, term, relaxedTo);
    } catch (err) {
      if (err.name !== 'AbortError') this.close();
    }
  }

  /* --- Rendering --- */

  group(title, items, mod) {
    if (!items.length) return '';
    return `<div class="wubs-search__group${mod ? ' wubs-search__group--' + mod : ''}">
      <h2 class="wubs-search__group-title">${wubsEscape(title)}</h2>
      <ul class="wubs-search__list" role="list">${items.join('')}</ul>
    </div>`;
  }

  static row(href, thumbHtml, title, meta, trail) {
    return `<li class="wubs-search__item" role="option">
      <a href="${wubsEscape(href)}">
        ${thumbHtml}
        <span class="wubs-search__body">
          <span class="wubs-search__title">${wubsEscape(title)}</span>
          ${meta ? `<span class="wubs-search__meta">${wubsEscape(meta)}</span>` : ''}
        </span>
        ${trail || ''}
      </a>
    </li>`;
  }

  productRow(p) {
    const img = p.featured_image?.url || p.image;
    const thumb = img
      ? `<img class="wubs-search__thumb" src="${wubsEscape(img)}&width=120" alt="" loading="lazy" width="44" height="44">`
      : `<span class="wubs-search__thumb wubs-search__thumb--icon">${WubsSearch.icons.box}</span>`;
    // suggest.json ger priset i kronor som sträng ("129.00") – kampanjuträkningen går i öre som korten.
    const ore = p.price != null ? Math.round(parseFloat(p.price) * 100) : null;
    const karta = window.WubsKampanj?.direkt();
    const kampanj = ore != null && karta && p.handle ? window.WubsKampanj.pris(ore, karta[p.handle]) : null;
    let trail = '';
    if (kampanj != null) {
      trail = `<span class="wubs-search__price wubs-search__price--kampanj">${wubsEscape(wubsMoney.format(kampanj / 100))}` +
        ` <s class="wubs-search__price-ord">${wubsEscape(wubsMoney.format(ore / 100))}</s></span>`;
    } else if (ore != null) {
      trail = `<span class="wubs-search__price">${wubsEscape(wubsMoney.format(ore / 100))}</span>`;
    }
    return WubsSearch.row(p.url, thumb, p.title, p.available === false ? 'Tillfälligt slut' : '', trail);
  }

  render(r, term, relaxedTo) {
    // Kampanjindexet (assets/wubs-kampanj.js) hämtas första gången det behövs. Är det inte
    // färdigt när träffarna kommer renderas raderna med ordinarie pris och byggs om EN gång när
    // det landat – bara om svaret fortfarande är det som visas, och bara om någon träff har kampanj.
    this.senasteRender = r;
    if (window.WubsKampanj && !window.WubsKampanj.direkt()) {
      window.WubsKampanj.index().then((karta) => {
        if (this.senasteRender !== r) return;
        if ((r.products || []).some((p) => karta[p.handle])) this.render(r, term, relaxedTo);
      });
    }

    // Konfiguratorerna är måttbeställningen – de förtjänar en egen grupp överst
    const isConf = (p) => /konfigurator/i.test(p.title || '');
    const confs = (r.products || []).filter(isConf).slice(0, 3);
    const prods = (r.products || []).filter((p) => !isConf(p)).slice(0, this.limit);
    const help = [...(r.articles || []), ...(r.pages || [])].slice(0, this.limit);
    // Kampanjkollektioner är ADMINYTOR – de måste vara publicerade för att Liquid ska se dem
    // (en opublicerad kollektion är osynlig i Liquid, uppmätt), men de ska aldrig visas för en
    // kund. suggest.json ger inga metafält, så filtret går på handtaget: en kampanjkollektion
    // SKA heta kampanj-*. Utan det här listas "Kampanj – TEST beslag" vid sökning på "beslag".
    const cols = (r.collections || [])
      .filter((c) => !/^\/collections\/kampanj-/.test(c.url || ''))
      .slice(0, this.limit);

    const left = [
      this.group(
        'Måttbeställ',
        confs.map((p) =>
          WubsSearch.row(
            p.url,
            `<span class="wubs-search__thumb wubs-search__thumb--icon">${WubsSearch.icons.ruler}</span>`,
            (p.title || '').replace(/\s*[-–]\s*konfigurator/i, ''),
            'Konfigurator',
            ''
          )
        ),
        'conf'
      ),
      this.group(
        'Kategorier',
        cols.map((c) => {
          /* ⚠️ 26 av 44 kollektioner saknar egen bild. Suggest-API:t gör INGEN fallback – det
             skickar `featured_image: { url: null }`. Vi renderar därför ikonen först och byter
             till första produktens bild när den hämtats (se fyllKollektionsbilder). Att bygga
             kartan i Liquid i stället hade tvingat headern att ladda produkter för 26
             kollektioner på VARJE sidvisning. */
          const bild = c.featured_image?.url || null;
          const thumb = bild
            ? `<img class="wubs-search__thumb" src="${wubsEscape(bild)}&width=120" alt="" loading="lazy" width="44" height="44">`
            : `<span class="wubs-search__thumb wubs-search__thumb--icon" data-col-thumb="${wubsEscape(c.handle || '')}">${WubsSearch.icons.box}</span>`;
          return WubsSearch.row(c.url, thumb, c.title, '', '');
        })
      ),
      this.group(
        'Guider & hjälp',
        help.map((a) => {
          /* Artiklar har `image` (och `featured_image`) i suggest-svaret – sidor har ingen
             bildnyckel alls och behåller ikonen. */
          const bild = a.image?.url || a.image || a.featured_image?.url || null;
          const thumb = bild
            ? `<img class="wubs-search__thumb" src="${wubsEscape(bild)}&width=120" alt="" loading="lazy" width="44" height="44">`
            : `<span class="wubs-search__thumb wubs-search__thumb--icon">${WubsSearch.icons.help}</span>`;
          return WubsSearch.row(a.url, thumb, a.title, a.summary_html != null ? 'Guide' : 'Sida', '');
        })
      ),
    ]
      .filter(Boolean)
      .join('');

    const right =
      this.group('Produkter', prods.map((p) => this.productRow(p))) + this.konfigKort(term, relaxedTo);

    if (!left && !right) {
      this.panel.innerHTML = `<p class="wubs-search__empty">
        Inga träffar på “${wubsEscape(term)}”.<br>
        Prova ett annat ord – eller ring oss på <a href="tel:0707611528">070-761 15 28</a> så hjälper vi dig.
      </p>`;
      this.open();
      return;
    }

    const note = relaxedTo
      ? `<p class="wubs-search__note">Inga träffar på “${wubsEscape(term)}”. Visar resultat för <strong>${wubsEscape(
          relaxedTo
        )}</strong>.</p>`
      : '';

    this.panel.innerHTML = `
      ${note}
      <div class="wubs-search__cols">
        <div class="wubs-search__col wubs-search__col--side">${left}</div>
        <div class="wubs-search__col wubs-search__col--main">${right}</div>
      </div>
      <div class="wubs-search__footer">
        <a href="${window.Shopify?.routes?.root || '/'}search?q=${encodeURIComponent(term)}">
          Visa alla träffar för “${wubsEscape(term)}”
        </a>
      </div>`;
    this.fyllKollektionsbilder();
    this.open();
  }

  /* Kollektioner utan egen bild får första produktens bild. Ett litet anrop per kollektion,
     cachat för hela sessionen – oftast 1–3 per sökning.
     ⚠️ Ikonen står kvar tills bilden finns; misslyckas anropet händer ingenting alls. En
     tom ruta vore sämre än den generiska ikonen. */
  fyllKollektionsbilder() {
    const rutor = this.panel.querySelectorAll('[data-col-thumb]');
    if (!rutor.length) return;
    WubsSearch.colBildCache = WubsSearch.colBildCache || new Map();
    const cache = WubsSearch.colBildCache;

    rutor.forEach((ruta) => {
      const handle = ruta.dataset.colThumb;
      if (!handle) return;

      const satt = (url) => {
        if (!url || !ruta.isConnected) return;
        const img = document.createElement('img');
        img.className = 'wubs-search__thumb';
        img.src = url.includes('?') ? `${url}&width=120` : `${url}?width=120`;
        img.alt = '';
        img.loading = 'lazy';
        img.width = 44;
        img.height = 44;
        ruta.replaceWith(img);
      };

      if (cache.has(handle)) return satt(cache.get(handle));

      const root = window.Shopify?.routes?.root || '/';
      fetch(`${root}collections/${encodeURIComponent(handle)}/products.json?limit=1`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          const url = d?.products?.[0]?.images?.[0]?.src || null;
          cache.set(handle, url);
          satt(url);
        })
        .catch(() => cache.set(handle, null));
    });
  }

  /* "Testa konfiguratorn" i högerkolumnen (Liam 2026-08-31). Söker kunden på "ledstång" är det
     oftast måttbeställningen hen är ute efter, inte en hyllvara – då ska vägen dit vara ett kort,
     inte en rad bland andra.
     ⚠️ Matchar på det RELAXERADE ordet också: skriver kunden "ledstång regler" faller frasen
     tillbaka på "ledstång" (se query-relaxeringen ovan), och då ska kortet fortfarande visas. */
  konfigKort(term, relaxedTo) {
    if (!WubsSearch.konfig) {
      const el = this.querySelector('[data-wubs-search-konfig]');
      try {
        WubsSearch.konfig = el ? JSON.parse(el.textContent) : [];
      } catch (e) {
        WubsSearch.konfig = [];
      }
    }
    if (!WubsSearch.konfig.length) return '';

    const q = `${term} ${relaxedTo || ''}`.toLowerCase();
    const träff = WubsSearch.konfig.find((k) => (k.ord || []).some((o) => q.includes(o)));
    if (!träff) return '';

    return `<a class="wubs-search__konfig" href="${wubsEscape(träff.url)}">
      <span class="wubs-search__konfig-icon">${WubsSearch.icons.ruler}</span>
      <span class="wubs-search__konfig-body">
        <span class="wubs-search__konfig-kicker">Måttbeställ</span>
        <span class="wubs-search__konfig-title">${wubsEscape(träff.titel)}</span>
        <span class="wubs-search__konfig-text">${wubsEscape(träff.text)}</span>
      </span>
      <span class="wubs-search__konfig-cta">Öppna</span>
    </a>`;
  }

  open() {
    this.panel.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
    this.satHojd();
  }

  /* Panelens takhöjd på mobil (Liam 2026-08-31: "låt sökrutan ta 85% av skärmhöjden").
     ⚠️ Den kan inte vara `max-height: 85dvh` rätt av: panelen BÖRJAR under headern och
     sökfältet (~186 px på 375), så 85 % av skärmen mätt därifrån hade slutat långt utanför
     skärmkanten. Vi vill att panelens UNDERKANT ska landa på 85 % – alltså
     85 % av höjden MINUS var panelen börjar.
     ⚠️ Mäts vid öppning i stället för att gissa headerns höjd: utility-baren kan vara av,
     logotypbredden är en inställning, och adressfältet på mobil ändrar den synliga höjden.
     CSS:en har en fallback om det här aldrig körs. */
  satHojd() {
    const vh = window.visualViewport?.height || document.documentElement.clientHeight || window.innerHeight;
    if (!vh) return;
    const topp = this.panel.getBoundingClientRect().top;
    if (topp <= 0) return;
    const max = Math.round(vh * 0.85 - topp);
    if (max > 160) this.panel.style.setProperty('--wubs-search-max', max + 'px');
  }

  close() {
    this.panel.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
  }

  reset() {
    this.input.value = '';
    this.clearBtn.hidden = true;
    this.close();
  }
}

WubsSearch.icons = {
  search:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5"/><path d="m15.5 15.5 4.5 4.5"/></svg>',
  box: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3.2 20 7v10l-8 3.8L4 17V7l8-3.8Z"/><path d="M4 7l8 3.8L20 7M12 10.8V20.8"/></svg>',
  help: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="8.5"/><path d="M9.6 9.4a2.4 2.4 0 1 1 4.6 1c0 1.6-2.2 2-2.2 3.5"/><path d="M12 17.2h.01"/></svg>',
  ruler:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="1.8" y="8.2" width="20.4" height="7.6" rx="1.2"/><path d="M6.6 8.2v3M10.2 8.2v2M13.8 8.2v3M17.4 8.2v2"/></svg>',
};

if (!customElements.get('wubs-search')) customElements.define('wubs-search', WubsSearch);

/* ------------------------------------------------------------------ *
 * Header: megameny + mobil drawer
 * ------------------------------------------------------------------ */
/* Mobilmenyns accordion: mjuk höjd-animation ÅT BÅDA HÅLLEN (Liam 2026-09-29 "kan vi även stänga
   dessa mjukt"). Förut var öppningen en 0,2 s fade medan höjden snappade, och stängningen var ett
   rakt `hidden` – panelen hoppade ihop. Nu animeras höjden 0↔scrollHeight precis som PDP-accordion
   (wubs-product.js): SAMMA längd och SAMMA kurvor, annars känns sajtens två dragspel som två olika.

   ⚠️ `hidden` måste bort INNAN höjden mäts – ett dolt element har scrollHeight 0 och animationen
   hade gått från 0 till 0. Av samma skäl sätts `hidden` tillbaka först när stängningen är klar.
   ⚠️ Failsafe-timern måste finnas: transitionend uteblir om drawern stängs mitt i rörelsen, och
   utan den hade undermenyn blivit låst i halvhöjd med kvarglömda inline-stilar.
   ⚠️ prefers-reduced-motion → växla direkt, ingen rörelse alls. */
const AKK_DUR = 300;
const AKK_EASE_OPEN = 'cubic-bezier(0, 0, 0.2, 1)';   // bromsar in – mjuk landning
const AKK_EASE_CLOSE = 'cubic-bezier(0.4, 0, 1, 1)';  // accelererar ut – ingen krypande sista pixel

function akkordeon(sub, open) {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    sub.hidden = !open;
    return;
  }

  sub.dataset.animating = '1';
  sub.style.overflow = 'hidden';

  if (open) {
    sub.hidden = false;
    const h = sub.scrollHeight;
    sub.style.height = '0px';
    sub.style.opacity = '0';
    void sub.offsetWidth; // tvinga reflow (förhandsgranskningspanen throttlar rAF)
    // innehållet tonas in strax EFTER att höjden börjat → mjuk start
    sub.style.transition =
      'height ' + AKK_DUR + 'ms ' + AKK_EASE_OPEN + ', opacity ' + AKK_DUR + 'ms ease ' + Math.round(AKK_DUR * 0.15) + 'ms';
    sub.style.height = h + 'px';
    sub.style.opacity = '1';
  } else {
    sub.style.height = sub.scrollHeight + 'px';
    sub.style.opacity = '1';
    void sub.offsetWidth;
    // innehållet tonas ut FÖRE höjden når 0 → sista biten kollapsar tom
    sub.style.transition =
      'height ' + AKK_DUR + 'ms ' + AKK_EASE_CLOSE + ', opacity ' + Math.round(AKK_DUR * 0.55) + 'ms ease';
    sub.style.height = '0px';
    sub.style.opacity = '0';
  }

  let klar = false;
  const stad = () => {
    if (klar) return;
    klar = true;
    if (!open) sub.hidden = true;
    sub.style.transition = '';
    sub.style.height = '';
    sub.style.opacity = '';
    sub.style.overflow = '';
    delete sub.dataset.animating;
    sub.removeEventListener('transitionend', vidSlut);
  };
  // lyssna på HÖJDEN specifikt – opacity slutar tidigare och hade städat för fort
  const vidSlut = (e) => { if (e.propertyName === 'height') stad(); };
  sub.addEventListener('transitionend', vidSlut);
  setTimeout(stad, AKK_DUR + 80);
}

class WubsHeader extends HTMLElement {
  constructor() {
    super();
    this.openItem = null;
    this.closeTimer = null;
  }

  connectedCallback() {
    this.initMega();
    this.initDrawer();
    this.initSearchToggle();
    this.initAccount();
    this.matToolsReserv();
    this.initAnnouncement();
    this.initScrollHide();
    this.initMenuLock();

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeMega();
        this.closeDrawer();
        this.closeSearch();
      }
    });
  }

  /* --- Scroll-låset för menyerna (Liam 2026-10-21 "när megameny på desktop eller dropdown är
     framme, så kan man fortfarande skrolla på sidan, det ska inte gå") ---
     Megamenyn, navets dropdown, kontomenyn och söket låser sidan med SAMMA lås som lådorna
     (WubsScroll, <html>, djupräknare, scrollbar-kompensation).
     ⚠️⚠️ Headern äger HÖGST ETT lås (`_menuLas`). Menyerna öppnas och stängs från många håll
     (hover, klick, Esc, fokus, söket stänger megan …) – hade varje väg anropat las()/slapp() själv
     hade räknaren hamnat i otakt och antingen släppt en öppen varukorgslåda eller lämnat sidan
     låst. I stället räknar `syncMenuLock()` ut om NÅGON meny syns just nu och tar/släpper bara
     headerns eget lås vid en övergång. Anropa den efter varje ändring av en menys läge.
     ⚠️ Kontomenyn visas av CSS (:hover / :focus-within / .is-open) – läget läses därför med
     `matches()`, inte ur en flagga, så låset speglar exakt det som syns.
     ⚠️ Navet finns bara från 1200 px och kontoknappen från 990 px: menyerna stängs när fönstret
     krymper under respektive gräns.
     ⚠️ bfcache: klickar man en länk i megamenyn och går tillbaka återställs sidan MED öppen meny
     och låst <html> – `pageshow` stänger allt. */
  initMenuLock() {
    // Samma brytpunkter som CSS:en: navet döljs under 1200 px, kontoknappen under 990 px.
    window.matchMedia('(max-width: 1199px)').addEventListener?.('change', (e) => { if (e.matches) this.closeMega(); });
    window.matchMedia('(max-width: 989px)').addEventListener?.('change', (e) => { if (e.matches) this.closeAccount?.(); });
    window.addEventListener('pageshow', (e) => {
      if (!e.persisted) return;
      this.closeMega();
      this.closeAccount?.();
      this.closeSearch();
      this.syncMenuLock();
    });
    window.addEventListener('resize', () => { if (this.openItem) this.fitMega(this.openItem); }, { passive: true });
  }

  menuOpen() {
    if (this.openItem) return true;
    if (this.searchBar && !this.searchBar.hidden) return true;
    const acct = this.acct;
    return !!(acct && acct.classList.contains('is-authed') && acct.offsetParent !== null &&
      (acct.classList.contains('is-open') || acct.matches(':hover') || acct.matches(':focus-within')));
  }

  /* Räknas i en mikrotask: att gå från en meny till nästa (A stängs → B öppnas, söket stängs →
     megan öppnas) hade annars släppt och tagit låset igen i samma ögonblick. */
  syncMenuLock() {
    if (this._syncKo) return;
    this._syncKo = true;
    queueMicrotask(() => {
      this._syncKo = false;
      if (!window.WubsScroll) return;
      const open = this.menuOpen();
      if (open === !!this._menuLas) return;
      this._menuLas = open;
      if (open) WubsScroll.las();
      else WubsScroll.slapp();
    });
  }

  /* Sidan bakom är låst → en mega högre än fönstret måste kunna scrollas själv. Bara då får den
     max-höjd + egen scroll; annars rörs ingenting (oftast ryms den). `.wubs-header__mega` har
     redan data-lenis-prevent (wubs-lenis.js), så hjulet når panelen trots att Lenis är stoppad.
     offsetParent + offsetTop i stället för panelens egen rect – öppningsanimationen startar 8 px
     för högt och hade gett en för stor höjd. */
  fitMega(item) {
    const panel = item.querySelector('.wubs-header__mega');
    if (!panel || panel.hidden) return;
    panel.style.maxHeight = '';
    panel.style.overflowY = '';
    const op = panel.offsetParent;
    const top = op ? op.getBoundingClientRect().top + panel.offsetTop : panel.getBoundingClientRect().top;
    const avail = Math.floor(window.innerHeight - top);
    if (avail > 120 && panel.offsetHeight > avail) {
      panel.style.maxHeight = avail + 'px';
      panel.style.overflowY = 'auto';
    }
  }

  /* --- Annonsraden glider bort vid scroll nedåt och tillbaka vid scroll uppåt (Liam 2026-09-11) ---
     Hela header-sektionen är sticky. I stället för att dölja raden (höjdändring = sidan hoppar)
     flyttas sektionens sticky-`top` upp med radens höjd: raden glider ut ovanför skärmkanten
     medan huvudraden står kvar överst.
     ⚠️⚠️ `top`, INTE transform. En transform på en förfader gör den till containing block för
     position: fixed-barn – mobilmenyn och sökets overlay hade då lagt sig inuti headern i
     stället för över hela skärmen.
     ⚠️ Klistrade element under headern (kollektionens verktygsrad + filter, artikelns och sidans
     innehållsförteckning) läser --wubs-hh-dold och flyttar upp lika mycket – annars hade det
     blivit en glipa lika hög som raden mellan headern och dem.
     ⚠️ Rörelsen samlas i en riktning innan något händer (14 px), så att tröghetsscroll och
     studsar i sidans kant inte får raden att blinka. */
  initScrollHide() {
    if (this.dataset.sticky !== 'true') return;
    const sek = this.closest('.shopify-section');
    const rad = this.querySelector('.wubs-header__utility');
    if (!sek || !rad) return;
    const rot = document.documentElement;
    let radH = 0;
    let dold = false;
    const mat = () => {
      radH = Math.round(rad.getBoundingClientRect().height);
      rot.style.setProperty('--wubs-utility-h', radH + 'px');
      rot.style.setProperty('--wubs-hh-dold', dold ? radH + 'px' : '0px');
    };
    const satt = (nytt) => {
      if (nytt === dold) return;
      dold = nytt;
      sek.classList.toggle('is-utility-dold', dold);
      rot.style.setProperty('--wubs-hh-dold', dold ? radH + 'px' : '0px');
    };
    mat();
    window.addEventListener('resize', mat, { passive: true });
    let forra = window.scrollY;
    let samlat = 0;
    window.addEventListener('scroll', () => {
      const y = window.scrollY;
      const dy = y - forra;
      forra = y;
      // Inom radens egen höjd från toppen syns den alltid – annars står ett hål där den låg.
      if (y <= radH) { samlat = 0; satt(false); return; }
      samlat = (dy > 0) === (samlat > 0) ? samlat + dy : dy;
      if (samlat > 14) satt(true);
      else if (samlat < -14) satt(false);
    }, { passive: true });
  }

  /* --- Roterande meddelande i utility-baren (Liam 2026-08-31) ---
     Flera block delar samma yta och tonas växelvis. Ingen extern slider: tre rader logik och
     inga beroenden är rätt storlek på problemet.
     ⚠️ Pausar vid hover OCH när fliken är dold – annars hinner meddelandena rulla förbi medan
     kunden är någon annanstans, och hen kommer tillbaka mitt i en tona-in.
     ⚠️ Respekterar `prefers-reduced-motion` genom att INTE rotera alls: en rad som byter text
     av sig själv är rörelse, även om övergången är en toning. */
  initAnnouncement() {
    const rad = this.querySelector('[data-wubs-ann]');
    if (!rad) return;
    const poster = rad.querySelectorAll('.wubs-header__ann-item');
    if (poster.length < 2) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const intervall = parseInt(rad.dataset.interval, 10) || 5000;
    let i = 0;
    let timer = null;

    const visa = (n) => {
      poster.forEach((p, x) => p.classList.toggle('is-aktiv', x === n));
    };
    const start = () => {
      if (timer) return;
      timer = setInterval(() => {
        if (document.hidden) return;
        i = (i + 1) % poster.length;
        visa(i);
      }, intervall);
    };
    const stopp = () => {
      clearInterval(timer);
      timer = null;
    };

    rad.addEventListener('mouseenter', stopp);
    rad.addEventListener('mouseleave', start);
    rad.addEventListener('focusin', stopp);
    rad.addEventListener('focusout', start);
    document.addEventListener('visibilitychange', () => (document.hidden ? stopp() : start()));
    start();
  }

  /* --- Reserverad bredd för ikonkolumnen ---
     Huvudraden är gridden `logga | nav | ikoner`. Fälls en pill ut växer ikonkolumnen,
     mittkolumnen krymper och NAVET VANDRAR I SIDLED. Vi reserverar därför alltid plats för det
     bredaste utfällda läget, så kolumnbredden aldrig ändras.

     ⚠️ Mätt i stället för hårdkodat: etiketterna är redaktörstext och fonten (Helvetica) laddas
     asynkront, så ett fast tal i CSS:en driver isär. CSS:en har ändå en uppmätt fallback om det
     här aldrig körs. Samma mönster som `--wubs-hh` på kollektionssidan.
     ⚠️ Etiketten är `max-width: 0` i vila, så den måste mätas på en KLON utan begränsningen –
     `scrollWidth` på originalet ger 0 när den är hopfälld. */
  matToolsReserv() {
    const tools = this.querySelector('.wubs-header__tools');
    if (!tools) return;

    const mat = () => {
      const texter = tools.querySelectorAll('.wubs-header__tool-text');
      if (!texter.length) return;

      /* ⚠️ Fråga CSS:en, inte `window.innerWidth`. Under 990px är etiketterna dolda och inget kan
         växa – men förhandsgranskningspanen rapporterar transient `innerWidth: 0`, och en
         breddbaserad vakt stänger då av reserven på en desktop-vy. Att läsa etikettens faktiska
         `display` ger rätt svar oavsett vad viewporten påstår. Samma grepp som kollektionssidans
         filtertoggle. */
      if (getComputedStyle(texter[0]).display === 'none') {
        tools.style.removeProperty('--wubs-tools-reserve');
        return;
      }

      let bredast = 0;
      texter.forEach((t) => {
        const klon = t.cloneNode(true);
        klon.style.cssText = 'max-width:none;opacity:0;margin:0;position:absolute;visibility:hidden;pointer-events:none';
        document.body.appendChild(klon);
        bredast = Math.max(bredast, klon.getBoundingClientRect().width);
        klon.remove();
      });
      if (!bredast) return;

      /* ⚠️⚠️ Mät det KOLLAPSADE läget utan reserven påslagen. Läser man bredden medan
         min-width redan gäller blir reserven sin egen indata och växer vid varje ommätning
         (resize → bredare → resize → ännu bredare). En storhet som räknas om måste nollställas
         först – annars är den en integrator. */
      const forra = tools.style.minWidth;
      tools.style.minWidth = '0px';
      const kollapsad = tools.getBoundingClientRect().width;

      /* ⚠️⚠️ KLAMPA MOT LEDIGT UTRYMME. Reserven får aldrig ta plats som inte finns: under
         ~1200px ryms logga + nav + ikoner redan nätt och jämnt, och en oklampad reserv trycker
         navet in över loggan och ikonerna. Uppmätt vid 1000px: överlappet var 61px utan reserv
         och blev 104px med. Bättre att navet vandrar lite på trånga bredder än att det krockar.
         (Överlappet i sig är ett ÄLDRE fel – navet är för brett där – och löses inte här.) */
      const inner = this.querySelector('.wubs-header__main-inner');
      const nav = this.querySelector('.wubs-header__nav');
      const logo = this.querySelector('.wubs-header__logo, .wubs-header__logo-heading');
      /* +18: 10 px är etikettens margin-right vid utfällning, resten är slack. Med bara
         marginalen låg reserven 1 px över behovet, och då räcker en avrundning eller en
         omladdad font för att den ska brista. */
      let onskad = bredast + 18;
      if (inner && nav && logo) {
        const gap = parseFloat(getComputedStyle(inner).columnGap) || 0;
        const ledigt =
          inner.getBoundingClientRect().width -
          logo.getBoundingClientRect().width -
          nav.getBoundingClientRect().width -
          kollapsad -
          gap * 2;
        onskad = Math.max(0, Math.min(onskad, ledigt));
      }
      tools.style.minWidth = forra;

      const reserv = Math.ceil(kollapsad + onskad);
      tools.style.setProperty('--wubs-tools-reserve', reserv + 'px');
    };

    // Mät när fonten är klar – annars mäts fallback-fonten och reserven blir för smal.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(mat);
    }
    mat();

    let t;
    window.addEventListener('resize', () => {
      clearTimeout(t);
      t = setTimeout(mat, 150);
    });
  }

  /* --- Kontodropdown --- */
  initAccount() {
    const acct = this.querySelector('[data-wubs-account]');
    if (!acct) return;
    this.acct = acct;
    const toggle = acct.querySelector('[data-wubs-account-toggle]');
    // Låset läses EFTER att webbläsaren uppdaterat :hover/:focus-within – därav timeouten.
    const sync = () => setTimeout(() => this.syncMenuLock(), 0);
    const closeMenu = () => {
      acct.classList.remove('is-open');
      toggle?.setAttribute('aria-expanded', 'false');
      sync();
    };
    // Esc och brytpunkten måste även ta bort fokus – :focus-within håller annars menyn framme.
    this.closeAccount = () => {
      if (acct.contains(document.activeElement)) document.activeElement.blur();
      closeMenu();
    };
    acct.addEventListener('mouseenter', sync);
    acct.addEventListener('mouseleave', sync);
    acct.addEventListener('focusin', sync);
    acct.addEventListener('focusout', sync);
    const setAuthed = () => {
      acct.classList.add('is-authed');
      const label = acct.querySelector('[data-wubs-account-label]');
      if (label && (!label.textContent || label.textContent.trim() === 'Logga in')) label.textContent = 'Mitt konto';
    };

    toggle?.addEventListener('click', (e) => {
      // Utloggad: klick öppnar Shopifys login i popup (WubsAuth bor i wishlist-drawer.js).
      if (!acct.classList.contains('is-authed')) {
        e.preventDefault();
        if (window.WubsAuth && typeof window.WubsAuth.login === 'function') window.WubsAuth.login();
        else window.location.href = '/account/login';
        return;
      }
      // Inloggad: klick togglar menyn (hover sköts av CSS).
      const open = !acct.classList.contains('is-open');
      acct.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', String(open));
      sync();
    });

    document.addEventListener('click', (e) => {
      if (!acct.contains(e.target)) closeMenu();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && (acct.classList.contains('is-open') || acct.contains(document.activeElement))) this.closeAccount();
    });
    // Efter popup-login utan omladdning: flippa till inloggat läge på plats.
    document.addEventListener('wubs:auth:login', setAuthed);
  }

  /* --- Megameny --- */
  initMega() {
    this.querySelectorAll('.wubs-header__nav-item--has-mega').forEach((item) => {
      const link = item.querySelector('.wubs-header__nav-link');
      const panel = item.querySelector('.wubs-header__mega');
      if (!link || !panel) return;

      const open = () => {
        clearTimeout(this.closeTimer);
        if (this.openItem && this.openItem !== item) this.closeMega();
        this.closeSearch();
        panel.hidden = false;
        item.classList.add('is-open');
        link.setAttribute('aria-expanded', 'true');
        this.classList.add('is-mega-open');
        this.openItem = item;
        this.fitMega(item);
        this.syncMenuLock();
      };
      const scheduleClose = () => {
        clearTimeout(this.closeTimer);
        this.closeTimer = setTimeout(() => this.closeMega(), 120);
      };

      item.addEventListener('mouseenter', open);
      item.addEventListener('mouseleave', scheduleClose);
      panel.addEventListener('mouseenter', () => clearTimeout(this.closeTimer));

      // Tangentbord: första trycket öppnar, andra följer länken
      link.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && panel.hidden) {
          e.preventDefault();
          open();
          panel.querySelector('a')?.focus();
        }
      });
      item.addEventListener('focusout', (e) => {
        if (!item.contains(e.relatedTarget)) scheduleClose();
      });
    });
  }

  closeMega() {
    this.classList.remove('is-mega-open');
    if (!this.openItem) return;
    const panel = this.openItem.querySelector('.wubs-header__mega');
    if (panel) panel.hidden = true;
    this.openItem.classList.remove('is-open');
    this.openItem.querySelector('.wubs-header__nav-link')?.setAttribute('aria-expanded', 'false');
    this.openItem = null;
    this.syncMenuLock();
  }

  /* --- Mobil drawer --- */
  initDrawer() {
    this.drawer = this.querySelector('#wubs-drawer');
    if (!this.drawer) return;

    this.querySelectorAll('[data-wubs-drawer-open]').forEach((btn) =>
      btn.addEventListener('click', () => this.openDrawer())
    );
    this.drawer.querySelectorAll('[data-wubs-drawer-close]').forEach((el) =>
      el.addEventListener('click', () => this.closeDrawer())
    );

    // Accordion
    this.drawer.querySelectorAll('.wubs-drawer__toggle').forEach((btn) => {
      btn.addEventListener('click', () => {
        const sub = btn.nextElementSibling;
        if (!sub) return;
        if (sub.dataset.animating) return;   // dubbelklick under rörelsen skulle mäta fel höjd
        const isOpen = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!isOpen));
        akkordeon(sub, !isOpen);
      });
    });
  }

  openDrawer() {
    // Redan öppen → gör ingenting. Annars hade WubsScroll.las() räknats upp en gång extra och
    // sidan förblivit låst efter stängningen (låset är en djupräknare, se wubs-scroll-lock.js).
    if (!this.drawer.hidden && this.drawer.classList.contains('is-open')) return;
    // ⚠️ Avbryt en pågående stängning. Utan detta hade timern nedan satt `hidden` på en meny
    //    som kunden just öppnat igen – och scroll-låset hamnat i otakt.
    clearTimeout(this._drawerTimer);
    this._drawerTimer = null;

    this.drawer.hidden = false;
    WubsScroll.las();   // låser <html>, inte <body> – annars tappar sticky sin scrollport
    /* Synkron reflow, inte requestAnimationFrame: rAF stryps när fliken inte målar och panelen
       fastnar då utanför kanten. Samma lösning som varukorgslådan och snabbköpet. */
    void this.drawer.offsetWidth;
    this.drawer.classList.add('is-open');
    this.querySelector('[data-wubs-drawer-open]')?.setAttribute('aria-expanded', 'true');
    this.drawer.querySelector('.wubs-drawer__close')?.focus();
  }

  closeDrawer() {
    // `_drawerTimer` = stängningen är redan igång; ett andra klick ska inte starta om den.
    if (!this.drawer || this.drawer.hidden || this._drawerTimer) return;
    /* Först klassen bort så panelen glider ut – `hidden` sätts FÖRST när övergången är klar.
       Sätts den direkt tas elementet bort mitt i rörelsen, vilket var hela problemet. */
    this.drawer.classList.remove('is-open');
    this.querySelector('[data-wubs-drawer-open]')?.setAttribute('aria-expanded', 'false');
    WubsScroll.slapp();
    this._drawerTimer = setTimeout(() => {
      this._drawerTimer = null;
      this.drawer.hidden = true;
    }, 220);   // = transitionen i wubs-header.css. Ändras den ena måste den andra följa med.
  }

  /* --- Sök: ikonknapp som fäller ner fältet under headern --- */
  initSearchToggle() {
    this.searchToggle = this.querySelector('[data-wubs-search-toggle]');
    this.searchBar = this.querySelector('#wubs-searchbar');
    if (!this.searchToggle || !this.searchBar) return;

    this.searchToggle.addEventListener('click', () => {
      if (this.searchBar.hidden) this.openSearch();
      else this.closeSearch();
    });

    // Overlayen (och klick utanför headern) stänger söket
    this.querySelectorAll('[data-wubs-search-close]').forEach((el) =>
      el.addEventListener('click', () => this.closeSearch())
    );
    document.addEventListener('click', (e) => {
      if (!this.searchBar.hidden && !this.contains(e.target)) this.closeSearch();
    });
  }

  openSearch() {
    // Värm kampanjindexet medan kunden skriver – då har de första träffarna rätt pris direkt.
    window.WubsKampanj?.index();
    this.closeMega();
    this.searchBar.hidden = false;
    this.searchToggle.setAttribute('aria-expanded', 'true');
    // Dimmar sidan – men inte headern (se .wubs-header__overlay i CSS:en)
    this.classList.add('is-searching');

    const search = this.searchBar.querySelector('wubs-search');
    search?.querySelector('input')?.focus();
    // Visa startläget explicit. Att förlita sig på focus-eventet är opålitligt
    // precis när fältet avdöljs – då blir rutan tom.
    search?.showStart?.();
    this.syncMenuLock();
  }

  closeSearch() {
    if (!this.searchBar || this.searchBar.hidden) return;
    this.searchBar.hidden = true;
    this.classList.remove('is-searching');
    this.searchToggle.setAttribute('aria-expanded', 'false');
    this.syncMenuLock();
  }
}

if (!customElements.get('wubs-header')) customElements.define('wubs-header', WubsHeader);

/* Mega med förhandsbild (Trappdelar-shop + Övriga/Träslag-listor): byt stor bild
   när man hovrar en kategori-rad. Scopas till närmaste mega så flera kan samexistera. */
document.addEventListener('mouseover', (e) => {
  const cat = e.target.closest('[data-mega-preview]');
  if (!cat) return;
  const key = cat.dataset.megaPreview;
  const mega = cat.closest('.wubs-header__mega');
  if (!mega) return;
  mega.querySelectorAll('[data-mega-preview]').forEach((c) => c.classList.toggle('is-active', c === cat));
  mega.querySelectorAll('[data-mega-preview-for]').forEach((p) =>
    p.classList.toggle('is-active', p.dataset.megaPreviewFor === key));
});

/* TEMAEDITORN (2026-09-18): genvägarna i mobilmenyn är block i headern, men menyn är stängd –
   markerade man "Genväg i mobilmenyn" ändrade man i blindo (samma lucka som varukorgslådan hade).
   Ett markerat block INUTI mobilmenyn öppnar den; avmarkering stänger. Bara editorn skickar
   shopify:*-händelserna. ⚠️ Vid ändrad inställning ritas headern om medan menyn är öppen – den
   gamla hinner aldrig släppa scroll-låset, därför nollställs det vid section:load. */
(() => {
  const iMeny = (e) => e.target && e.target.closest && e.target.closest('#wubs-drawer');
  const huvud = () => document.querySelector('wubs-header');
  document.addEventListener('shopify:block:select', (e) => {
    const h = huvud();
    if (iMeny(e) && h && h.drawer && h.drawer.hidden) h.openDrawer();
  });
  document.addEventListener('shopify:block:deselect', (e) => {
    const h = huvud();
    if (iMeny(e) && h && h.drawer && !h.drawer.hidden) h.closeDrawer();
  });
  document.addEventListener('shopify:section:load', (e) => {
    if (e.target && e.target.querySelector && e.target.querySelector('wubs-header') && window.WubsScroll) window.WubsScroll.slapp(true);
  });
})();

/* CHATTLÄNKEN I TOPPRADEN (2026-09-26, Liam "byt ut ring en trappexpert till chatta")
   Öppnar Shopifys chatt (Inbox). Widgeten är webbkomponenten <shopify-chat>, som Shopify
   injicerar på alla teman när chattkanalen är på; den har show()/close() och en `open`-flagga.

   ⚠️ Länken är en `tel:`-länk i markupen och uppgraderas HÄR. Saknas widgeten – chatten
   avstängd i Shopify, skriptet blockerat av en cookiebanner, eller JS av – får kunden ett
   telefonsamtal till samma trappexperter i stället för en knapp som inte gör någonting.
   ⚠️ Delegerad lyssnare på document: headern ritas om av temaeditorn och av Section Rendering,
   och en lyssnare bunden till elementet hade försvunnit med den gamla noden.
   ⚠️ Widgeten laddas asynkront. Finns den inte vid klicket väntar vi en kort stund på att den
   ska dyka upp innan vi ger upp och släpper igenom telefonlänken. */
(() => {
  const CHATT = 'shopify-chat, #ShopifyChat';

  function hittaChatt() {
    const el = document.querySelector(CHATT);
    return el && typeof el.show === 'function' ? el : null;
  }

  /* Väntar max ~1,2 s på widgeten. Längre än så är det bättre att ringa än att stå och vänta. */
  function vantaPaChatt(maxMs) {
    const start = Date.now();
    return new Promise((klar) => {
      (function kolla() {
        const el = hittaChatt();
        if (el || Date.now() - start > maxMs) return klar(el);
        setTimeout(kolla, 100);
      })();
    });
  }

  document.addEventListener('click', (e) => {
    const lank = e.target.closest && e.target.closest('[data-wubs-chat]');
    if (!lank) return;

    const direkt = hittaChatt();
    if (direkt) {
      e.preventDefault();
      direkt.show();
      return;
    }

    /* Ingen widget ÄNNU: stoppa telefonsamtalet, ge den en kort stund, ring annars ändå. */
    e.preventDefault();
    lank.setAttribute('aria-busy', 'true');
    vantaPaChatt(1200).then((el) => {
      lank.removeAttribute('aria-busy');
      if (el) el.show();
      else window.location.href = lank.getAttribute('href');
    });
  });
})();
